/**
 * Progreso visual compartido LoginPortal → handoff → bootstrap WMS.
 * sessionStorage comparte origen entre ambos frontends y sobrevive navigaciones completas.
 * Monotónico: nunca baja. Hitos reales + creep idle suave (sin saltos falsos).
 *
 * Companion `started_at`: TTL 60s. Progress v1 sigue numérico (no romper lectores).
 * Legado sin timestamp → clear (evita barra eterna tras restore WebView).
 */
export const LOGIN_TRANSITION_PROGRESS_KEY = 'wms_login_transition_progress_v1';
/** Epoch ms del inicio del intento visual (companion; no mezclar en la key numérica v1). */
export const LOGIN_TRANSITION_STARTED_AT_KEY = 'wms_login_transition_started_at_v1';
/** Techo de vida de la barra sticky en sessionStorage. */
export const LOGIN_TRANSITION_TTL_MS = 60_000;

export const AUTH_TRANSITION_EVENT = 'wms-auth-transition-change';
/** Overlay React ya pintó (boot HTML puede ceder sin hueco). */
export const AUTH_TRANSITION_PAINTED_EVENT = 'wms-auth-transition-painted';

/** Hitos visuales (solo UI; no bloquean auth). */
export const AUTH_TRANSITION_MILESTONES = Object.freeze({
  START: 10,
  PORTAL_OK: 35,
  SHELL_HANDOFF: 55,
  SESSION_OK: 75,
  /** Tope creep idle mientras esperamos red / bootstrap */
  IDLE_CAP: 88,
  DONE: 100,
});

export const AUTH_TRANSITION_FADE_MS = 450;

const IDLE_CREEP_STEP = 1;

const clamp = (value) => Math.min(100, Math.max(0, Number(value) || 0));

function dispatchTransitionChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_TRANSITION_EVENT));
  }
}

function readStartedAtMs() {
  try {
    const raw = sessionStorage.getItem(LOGIN_TRANSITION_STARTED_AT_KEY);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function ensureStartedAt() {
  try {
    if (sessionStorage.getItem(LOGIN_TRANSITION_STARTED_AT_KEY) == null) {
      sessionStorage.setItem(LOGIN_TRANSITION_STARTED_AT_KEY, String(Date.now()));
    }
  } catch {
    /* private mode / quota */
  }
}

function clearStartedAt() {
  try {
    sessionStorage.removeItem(LOGIN_TRANSITION_STARTED_AT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Si el progress sticky está vencido o es legado sin timestamp → limpia y retorna true.
 * @returns {boolean}
 */
function purgeStaleTransitionIfNeeded() {
  try {
    const progress = clamp(sessionStorage.getItem(LOGIN_TRANSITION_PROGRESS_KEY));
    if (progress <= 0) {
      if (readStartedAtMs() != null) clearStartedAt();
      return true;
    }
    const startedAt = readStartedAtMs();
    if (startedAt == null) {
      // Legado: progress sin companion → no confiar (barra eterna post-restore).
      sessionStorage.removeItem(LOGIN_TRANSITION_PROGRESS_KEY);
      clearStartedAt();
      dispatchTransitionChange();
      return true;
    }
    if (Date.now() - startedAt > LOGIN_TRANSITION_TTL_MS) {
      sessionStorage.removeItem(LOGIN_TRANSITION_PROGRESS_KEY);
      clearStartedAt();
      dispatchTransitionChange();
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function readLoginTransitionProgress() {
  try {
    if (purgeStaleTransitionIfNeeded()) return 0;
    return clamp(sessionStorage.getItem(LOGIN_TRANSITION_PROGRESS_KEY));
  } catch {
    return 0;
  }
}

export function writeLoginTransitionProgress(value) {
  const next = clamp(value);
  const current = readLoginTransitionProgress();
  const progress = Math.max(current, next);
  try {
    if (progress > 0) {
      ensureStartedAt();
      sessionStorage.setItem(LOGIN_TRANSITION_PROGRESS_KEY, String(progress));
    } else {
      sessionStorage.removeItem(LOGIN_TRANSITION_PROGRESS_KEY);
      clearStartedAt();
    }
  } catch {
    /* private mode / quota */
  }
  dispatchTransitionChange();
  return progress;
}

/**
 * Avanza a un hito nombrado (monotónico).
 * @param {keyof typeof AUTH_TRANSITION_MILESTONES | number} milestone
 */
export function markAuthTransitionMilestone(milestone) {
  const pct =
    typeof milestone === 'number'
      ? milestone
      : AUTH_TRANSITION_MILESTONES[milestone];
  if (pct == null || Number.isNaN(Number(pct))) return readLoginTransitionProgress();
  return writeLoginTransitionProgress(pct);
}

/** @deprecated alias semántico */
export function isLoginTransitionActive() {
  return isAuthTransitionVisible();
}

export function isAuthTransitionVisible() {
  return readLoginTransitionProgress() > 0;
}

export function startLoginTransitionProgress() {
  const current = readLoginTransitionProgress();
  if (current > 0) return current;
  return writeLoginTransitionProgress(AUTH_TRANSITION_MILESTONES.START);
}

/** Logout Tiger: misma barra continua hacia LoginPortal (no reinicia si ya avanzó). */
export function startLogoutTransition() {
  return startLoginTransitionProgress();
}

/**
 * Creep idle suave (+1, tope IDLE_CAP). Usar desde splash cuando el hito está estancado.
 * Ya no hace saltos grandes (+14/+9).
 */
export function advanceLoginTransitionProgress(current) {
  const value = clamp(current ?? readLoginTransitionProgress());
  if (value >= AUTH_TRANSITION_MILESTONES.IDLE_CAP) {
    return writeLoginTransitionProgress(AUTH_TRANSITION_MILESTONES.IDLE_CAP);
  }
  return writeLoginTransitionProgress(
    Math.min(AUTH_TRANSITION_MILESTONES.IDLE_CAP, value + IDLE_CREEP_STEP),
  );
}

export function clearLoginTransitionProgress() {
  clearAuthTransition();
}

export function clearAuthTransition() {
  try {
    sessionStorage.removeItem(LOGIN_TRANSITION_PROGRESS_KEY);
  } catch {
    /* ignore */
  }
  clearStartedAt();
  dispatchTransitionChange();
}

/**
 * Barra → 100%, fade-out breve, luego clear (bootstrap/login completado).
 * No alarga el login: corre después de permissionsReady.
 * @param {{ onBeforeClear?: () => void }} [opts]
 */
export function clearAuthTransitionWithFade(opts = {}) {
  if (typeof window === 'undefined') {
    clearAuthTransition();
    return Promise.resolve();
  }
  if (!isAuthTransitionVisible()) return Promise.resolve();

  writeLoginTransitionProgress(AUTH_TRANSITION_MILESTONES.DONE);
  opts.onBeforeClear?.();

  return new Promise((resolve) => {
    window.setTimeout(() => {
      clearAuthTransition();
      resolve();
    }, AUTH_TRANSITION_FADE_MS);
  });
}

/** Lee progreso para boot-splash HTML (sin side effects). */
export function readBootSplashProgressPercent() {
  const stored = readLoginTransitionProgress();
  return stored > 0 ? stored : AUTH_TRANSITION_MILESTONES.START;
}

export function awaitNextPaintFrames(count = 2) {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    let remaining = count;
    const step = () => {
      remaining -= 1;
      if (remaining <= 0) resolve();
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}
